import React, { useCallback, useMemo, useState } from "react";
import { Button, Stack } from "react-bootstrap";
import { Link } from "react-router";
import { Flex, Segmented } from "antd";
import { useForm, Controller as RHFController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Col, Container, Row, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  UserIdVerificationIcon,
  CalendarCheckIn01FreeIcons,
  Search01FreeIcons,
} from "@hugeicons/core-free-icons";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { PageHeader } from "@/CommonElements/PageTitle";
import { Controllers } from "@/CommonElements/Controller";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { AntInput } from "@/CommonElements/AntInput";
import { LabelValue } from "@/Component/Value";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useAuthContext } from "@/_helper/Auth";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useSystem } from "@/utils/hooks/useLocation";
import {
  useCreateNusukCardsBatchMutation,
  useNusukBatchLookupMutation,
} from "@/services/nusukCards/nusukCards";
import { handleFormErrors } from "@/utils/helpers";
import { DATE_TIME_FORMAT } from "@/utils/constants";

const COL_LAYOUT = { colProps: { xs: 12, md: 4 } };
const COL_LAYOUT_HALF = { colProps: { xs: 12, md: 6 } };
const LOOKUP_TYPES = { campaign: "campaign", group: "group" };

const DEFAULT_FORM_VALUES = {
  group_name_select_id: undefined,
  mapped_group_name: null,
  group_name: null,
};

export const EnterNewReceivedBatchesData = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  const { user } = useAuthContext();
  const { getLocalizedText } = useLocalizedText();

  const backLink = `/app/${currentSystem === "madina" ? "madina" : "makkah"}/nusuk-cards`;
  const now = dayjs().format(DATE_TIME_FORMAT);
  const userName = user?.name || user?.username || "User Name";

  const { mutate: createBatch, isPending } =
    useCreateNusukCardsBatchMutation();
  const { mutate: lookupBatch, isPending: isLookupPending } =
    useNusukBatchLookupMutation();

  const [lookupType, setLookupType] = useState(LOOKUP_TYPES.campaign);
  const [lookupData, setLookupData] = useState(null);
  const [pilgrimGroups, setPilgrimGroups] = useState([]);

  const form = useForm({
    disabled: isPending,
    defaultValues: DEFAULT_FORM_VALUES,
  });
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    getValues,
  } = form;

  const isGroupLookup = lookupType === LOOKUP_TYPES.group;
  const groupNameSelectId = watch("group_name_select_id");
  const entity = lookupData?.campaign || lookupData?.group || null;

  // ─── Derived options ───────────────────────────────────────────────

  const mappedGroupOptions = useMemo(
    () =>
      pilgrimGroups.map((g) => ({
        value: g.id,
        label: g.mapped_group_name,
      })),
    [pilgrimGroups]
  );

  const selectedMappedGroup = useMemo(
    () =>
      pilgrimGroups.find(
        (g) => String(g.id) === String(groupNameSelectId)
      ) ?? null,
    [pilgrimGroups, groupNameSelectId]
  );

  const rawGroupNameOptions = useMemo(
    () =>
      (selectedMappedGroup?.raw_group_names ?? []).map((name) => ({
        value: name,
        label: name,
      })),
    [selectedMappedGroup]
  );

  // ─── Handlers ──────────────────────────────────────────────────────

  const resetGroupFields = useCallback(() => {
    setValue("group_name", null);
    setValue("group_name_select_id", null);
    setValue("mapped_group_name", null);
  }, [setValue]);

  const handleLookupTypeChange = useCallback(
    (val) => {
      setLookupType(val);
      setLookupData(null);
      setPilgrimGroups([]);
      form.reset();
    },
    [form]
  );

  const applyLookupData = useCallback(
    (res) => {
      const data = res?.data?.data || res?.data || {};
      setLookupData(data);

      const ent = data.campaign || data.group || {};
      if (ent.service_center?.id != null)
        setValue("service_center_id", ent.service_center.id);
      if (ent.mission?.id != null)
        setValue("organizer_id", ent.mission.id);
      if (ent.nationality?.id != null)
        setValue("nationality_id", ent.nationality.id);

      const groups = Array.isArray(data.pilgrim_group_names)
        ? data.pilgrim_group_names
        : [];
      setPilgrimGroups(groups);
      resetGroupFields();
    },
    [setValue, resetGroupFields]
  );

  const handleLookup = useCallback(
    (type) => {
      const number = getValues("campaign_group_number");
      if (!number?.toString().trim()) {
        toast.warning(t("FIELD_REQUIRED"));
        return;
      }

      lookupBatch(
        { type, number: number.toString().trim() },
        {
          onSuccess: applyLookupData,
          onError: () =>
            toast.error(
              t("NO_RESULTS_FOUND", { defaultValue: t("NO_RESULTS") })
            ),
        }
      );
    },
    [getValues, lookupBatch, applyLookupData, t]
  );

  const onSubmit = (data) => {
    const payload = {
      batch_no: data.batch_no,
      entity: lookupType,
      campaign_id:
        Number(entity?.campaign_id) ||
        Number(entity?.campaign?.id) ||
        Number(entity?.id) ||
        Number(getValues("campaign_group_number")) ||
        null,
      group_name: data.group_name,
      card_no: data.card_no,
      representative_name: data.representative_name,
      notes: data.notes || null,
      service_center_id: entity?.service_center?.id || null,
      system: currentSystem,
    };

    createBatch(payload, {
      onSuccess: () => navigate(backLink),
      onError: (error) =>
        handleFormErrors(error, form.setError, t, "NUSUK_BATCH_CREATED_SUCCESSFULLY"),
    });
  };

  // ─── Form controllers ─────────────────────────────────────────────

  const formControllers = useMemo(() => {
    const ctrls = [
      {
        labelProps: { children: t("NUSUK_BATCH_NUMBER"), className: "fw-bold required" },
        controllerProps: {
          name: "batch_no",
          rules: { required: t("FIELD_REQUIRED") },
          render: ({ field }) => (
            <AntInput {...field} status={errors.batch_no ? "error" : undefined} />
          ),
        },
        layout: COL_LAYOUT,
      },

      ...(isGroupLookup
        ? [
            {
              labelProps: { children: t("MAPPED_GROUP_NAME"), className: "fw-bold required" },
              controllerProps: {
                name: "mapped_group_name",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field }) => (
                  <AntSelect
                    placeholder={t("MAPPED_GROUP_NAME")}
                    disabled={isPending}
                    options={mappedGroupOptions}
                    value={groupNameSelectId}
                    onChange={(val, option) => {
                      setValue("group_name_select_id", val ?? null, { shouldDirty: true });
                      field.onChange(option?.label ?? null);
                      setValue("group_name", null, { shouldDirty: true });
                    }}
                    onBlur={field.onBlur}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    status={errors.mapped_group_name ? "error" : undefined}
                  />
                ),
              },
              layout: COL_LAYOUT,
            },
            {
              labelProps: { children: t("GROUP_NAME"), className: "fw-bold required" },
              controllerProps: {
                name: "group_name",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field }) => (
                  <AntSelect
                    placeholder={t("GROUP_NAME")}
                    // disabled={isPending || !rawGroupNameOptions.length}
                    options={rawGroupNameOptions}
                    value={field.value}
                    onChange={(val) => field.onChange(val ?? null)}
                    onBlur={field.onBlur}
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                    status={errors.group_name ? "error" : undefined}
                  />
                ),
              },
              layout: COL_LAYOUT,
            },
          ]
        : []),

      {
        labelProps: { children: t("NUSUK_CARDS_COUNT"), className: "fw-bold required" },
        controllerProps: {
          name: "card_no",
          rules: {
            required: t("FIELD_REQUIRED"),
            validate: (value) => {
              if (!value) return true;
              if (!/^\d+$/.test(value))
                return t("MUST_BE_NUMBERS_DIGITS", { defaultValue: "Must be numbers only" });
              if (String(value).length > 3)
                return t("NUSUK_MAX_DIGITS", { max: 3 });
              return true;
            },
          },
          render: ({ field }) => (
            <AntInput {...field} maxLength={3} status={errors.card_no ? "error" : undefined} />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: { children: t("NUSUK_REPRESENTATIVE_NAME"), className: "fw-bold required" },
        controllerProps: {
          name: "representative_name",
          rules: {
            required: t("FIELD_REQUIRED"),
            maxLength: { value: 30, message: t("NUSUK_MAX_CHARS", { max: 30 }) },
          },
          render: ({ field }) => (
            <AntInput
              {...field}
              maxLength={30}
              status={errors.representative_name ? "error" : undefined}
            />
          ),
        },
        layout: COL_LAYOUT,
      },
      // رقم الخزينة
      {
        labelProps: { children: t("NUSUK_SAFE_NUMBER"), className: "fw-bold" },
        controllerProps: {
          name: "safe_number",
          render: ({ field }) => (
            <AntInput {...field} placeholder={t("NUSUK_SAFE_NUMBER")} />
          ),
        },
        layout: COL_LAYOUT,
      },
      {
        labelProps: { children: t("NOTES"), className: "fw-bold" },
        controllerProps: {
          name: "notes",
          render: ({ field }) => (
            <AntInput {...field} placeholder={t("NOTES")} status={errors.notes ? "error" : undefined} />
          ),
        },
        layout: COL_LAYOUT_HALF,
      },
    ];

    return ctrls.map((c) => ({
      ...c,
      controllerProps: { ...c.controllerProps, control, errors },
    }));
  }, [
    t,
    control,
    errors,
    isPending,
    isGroupLookup,
    mappedGroupOptions,
    groupNameSelectId,
    rawGroupNameOptions,
    setValue,
  ]);

  // ─── Render ────────────────────────────────────────────────────────

  return (
    <Stack gap={2}>
      <PageHeader
        title={t("NUSUK_RECEIVE_NEW_BATCH_SCREEN")}
        backTo={backLink}
        sticky={false}
      />

      <Container fluid>
        <div className="card border rounded-3 mb-4">
          <div className="card-body py-3 px-4">
            <Row>
              <Col xs={12} md={6}>
                <Flex align="center" gap={12}>
                  <Flex
                    align="center"
                    justify="center"
                    className="rounded-circle bg-light"
                    style={{ width: 40, height: 40, flexShrink: 0 }}
                  >
                    <HugeiconsIcon icon={UserIdVerificationIcon} size={20} className="text-primary" />
                  </Flex>
                  <LabelValue label={t("NUSUK_RECEIPT_CREATOR")} value={userName} direction="vertical" />
                </Flex>
              </Col>
              <Col xs={12} md={6}>
                <Flex align="center" gap={12}>
                  <Flex
                    align="center"
                    justify="center"
                    className="rounded-circle bg-light"
                    style={{ width: 40, height: 40, flexShrink: 0 }}
                  >
                    <HugeiconsIcon icon={CalendarCheckIn01FreeIcons} size={20} className="text-primary" />
                  </Flex>
                  <LabelValue
                    label={t("DATE")}
                    value={now}
                    direction="vertical"
                    renderValue={(v) => (
                      <span dir="ltr" style={{ unicodeBidi: "isolate" }}>{v}</span>
                    )}
                  />
                </Flex>
              </Col>
            </Row>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="card border rounded-3 mb-4 py-3 px-4">
            <Flex align="center" gap={12} className="mb-3">
              <Segmented
                value={lookupType}
                onChange={handleLookupTypeChange}
                options={[
                  {
                    label: <span className="mb-0 d-inline-block">{t("CAMPAIGN_NUMBER")}</span>,
                    value: LOOKUP_TYPES.campaign,
                  },
                  {
                    label: <span className="mb-0 d-inline-block">{t("GROUP_NUMBER")}</span>,
                    value: LOOKUP_TYPES.group,
                  },
                ]}
              />
            </Flex>

            <div className="d-flex align-items-end gap-3">
              <Stack gap={1} className="flex-grow-1">
                <label className="fw-bold required">
                  {isGroupLookup ? t("GROUP_NUMBER") : t("CAMPAIGN_NUMBER")}
                </label>
                <RHFController
                  name="campaign_group_number"
                  control={control}
                  rules={{ required: t("FIELD_REQUIRED") }}
                  render={({ field }) => (
                    <>
                      <AntInput
                        {...field}
                        placeholder={isGroupLookup ? t("GROUP_NUMBER") : t("CAMPAIGN_NUMBER")}
                        status={errors.campaign_group_number ? "error" : undefined}
                        onPressEnter={(e) => {
                          e.preventDefault();
                          handleLookup(lookupType);
                        }}
                      />
                      {errors.campaign_group_number && (
                        <div className="text-danger mt-1">
                          {errors.campaign_group_number.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </Stack>
              <Button
                variant="outline-primary"
                type="button"
                disabled={isLookupPending}
                onClick={() => handleLookup(lookupType)}
                style={{ height: 50, minWidth: 50 }}
              >
                {isLookupPending ? (
                  <Spinner size="sm" />
                ) : (
                  <HugeiconsIcon icon={Search01FreeIcons} size={18} />
                )}
              </Button>
            </div>

            {entity && (
              <div className="card border rounded-3 mb-4 mt-3">
                <div className="card-body py-3 px-4">
                  <Row>
                    <Col xs={12} md={6} lg={3} className="mb-3">
                      <LabelValue
                        label={t("CAMPAIGN_OR_GROUP_NAME")}
                        value={entity.name || "-"}
                        direction="vertical"
                      />
                    </Col>
                    <Col xs={12} md={6} lg={3} className="mb-3">
                      <LabelValue
                        label={t("SERVICE_CENTER")}
                        value={
                          entity.service_center
                            ? getLocalizedText(entity.service_center.name_ar, entity.service_center.name_en) ||
                              entity.service_center.name
                            : "-"
                        }
                        direction="vertical"
                      />
                    </Col>
                    <Col xs={12} md={6} lg={3} className="mb-3">
                      <LabelValue
                        label={t("ORGANIZER")}
                        value={
                          entity.mission
                            ? getLocalizedText(entity.mission.name_ar, entity.mission.name_en) ||
                              entity.mission.name
                            : "-"
                        }
                        direction="vertical"
                      />
                    </Col>
                    <Col xs={12} md={6} lg={3} className="mb-3">
                      <LabelValue
                        label={t("NATIONALITY")}
                        value={
                          entity.nationality
                            ? getLocalizedText(entity.nationality.name_ar, entity.nationality.name_en) ||
                              entity.nationality.name
                            : "-"
                        }
                        direction="vertical"
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </div>

          <Controllers controllers={formControllers} watch={watch} />

          <ActionsFooter>
            <Button className="btn btn-primary" type="submit" disabled={isPending}>
              {isPending ? <Spinner size="sm" /> : null}
              {t("SAVE")}
            </Button>
            <Link
              disabled={isPending}
              style={{ pointerEvents: isPending ? "none" : "auto" }}
              to={backLink}
              className="btn btn-danger"
            >
              {t("CANCEL")}
            </Link>
          </ActionsFooter>
        </form>
      </Container>
    </Stack>
  );
};
